import { useEffect, useState } from 'preact/hooks';

import { dateLabel, statusLabel, t } from '../i18n';
import { Icon } from './icons';

import type { ApiClient } from '../api';
import type { FeedbackItem, FeedbackType } from '../types';

/** Badge colour per status: our own names and the common Jira ones, falling back on the category. */
function badgeTone(status: FeedbackItem['status']): string {
  const name = status.name.toLowerCase();
  if (name.startsWith('won')) return 'muted';
  if (status.category === 'done') return 'done';
  if (status.category === 'new') return 'new';
  return 'busy';
}

export function StatusBadge({ status }: { status: FeedbackItem['status'] }) {
  return <span class={`hfb-badge hfb-badge-${badgeTone(status)}`}>{statusLabel(status.name)}</span>;
}

export function TypeLabel({ type }: { type: FeedbackType }) {
  const s = t();
  return <span class={`hfb-type hfb-type-${type}`}>{type === 'bug' ? s.bug : s.idea}</span>;
}

export function EmptyState(props: {
  icon: 'chat' | 'alert';
  tone?: 'danger';
  title: string;
  body: string;
  action?: { label: string; primary?: boolean; onClick: () => void };
}) {
  return (
    <div class="hfb-empty">
      <span class={`hfb-empty-icon${props.tone ? ` is-${props.tone}` : ''}`} aria-hidden="true">
        <Icon name={props.icon} size={20} />
      </span>
      <strong>{props.title}</strong>
      <p class="hfb-muted">{props.body}</p>
      {props.action && (
        <button
          type="button"
          class={`hfb-btn ${props.action.primary ? 'hfb-btn-primary' : 'hfb-btn-outline'}`}
          onClick={props.action.onClick}
        >
          {props.action.label}
        </button>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div class="hfb-skeleton" aria-busy="true">
      <div class="hfb-skeleton-head">
        <span />
        <span />
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} class="hfb-skeleton-row">
          <span />
          <span style={{ width: `${60 + ((i * 7) % 20)}%` }} />
        </div>
      ))}
    </div>
  );
}

export function MyFeedback({
  api,
  email,
  onOpen,
  onWrite,
}: {
  api: ApiClient;
  email: string;
  onOpen: (id: string) => void;
  onWrite: () => void;
}) {
  const s = t();
  const [items, setItems] = useState<FeedbackItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      setItems(await api.listMine(email));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [email]);

  if (failed) {
    return (
      <EmptyState
        icon="alert"
        tone="danger"
        title={s.loadFailedTitle}
        body={s.loadFailedBody}
        action={{ label: s.tryAgain, primary: true, onClick: () => void load() }}
      />
    );
  }
  if (!items) return <Skeleton />;
  if (!items.length) {
    return (
      <EmptyState
        icon="chat"
        title={s.emptyTitle}
        body={s.emptyBody}
        action={{ label: s.writeFeedback, onClick: onWrite }}
      />
    );
  }

  const unread = items.reduce((n, i) => n + i.unread, 0);
  return (
    <div class="hfb-list-wrap">
      <div class="hfb-list-head">
        <span>
          <strong>{s.reportsCount(items.length)}</strong>
          {unread > 0 && <span class="hfb-muted"> · {s.unreadReplies(unread)}</span>}
        </span>
        <button type="button" class="hfb-link-quiet" onClick={() => void load()} disabled={loading}>
          <Icon name="refresh" class={loading ? 'hfb-spin' : ''} />
          {s.refresh}
        </button>
      </div>
      <ul class="hfb-list">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              class={`hfb-row${item.unread ? ' is-unread' : ''}`}
              onClick={() => onOpen(item.id)}
            >
              <span class="hfb-row-main">
                <span class="hfb-row-meta">
                  <TypeLabel type={item.type} />
                  <span class="hfb-muted">{dateLabel(item.createdAt)}</span>
                </span>
                <span class="hfb-row-title">{item.title}</span>
                {item.unread > 0 && (
                  <span class="hfb-unread">
                    <span class="hfb-dot" aria-hidden="true" />
                    {s.unreadReplies(item.unread)}
                  </span>
                )}
              </span>
              <StatusBadge status={item.status} />
              <Icon name="chevronRight" class="hfb-row-chevron" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
